import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Button,
    Row,
    Col,
    Modal,
    message,
    Skeleton,
    Icon,
    Popover,
    Badge,
    Select,
    Dropdown,
    Menu,
} from "antd";
import styled from "styled-components";
import moment from "moment";

import "moment/locale/pt-br";

import { toBRL } from "~/components/UI/InputCurrency";
import { ModuleHeader, ModuleHeaderWrapper } from "~/components/ModuleHeader";
import Search from "~/_shared/components/Search";
import MonthPicker from "~/_shared/components/MonthPicker";
import TableList from "./Components/TableList";
import DrawerCreate from "./Components/DrawerCreate";
import * as contasPagarActions from "./actions";
import FilterPillsBar from "~/_shared/components/FilterPillsBar";
import RestoredFiltersNotice from "~/_shared/components/RestoredFiltersNotice";
import usePersistedFilters from "~/_shared/hooks/usePersistedFilters";
import {
    getDefaultContasPagarFilters,
    getContasPagarPeriodRequest,
    isContasPagarPeriodDefault,
    countActiveContasPagarFilters,
} from "./helpers";

var SummaryCard = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 10px;
    border: 1px solid #e8e8e8;
    transition: all 0.2s ease;
    height: 100%;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
`;

var SummaryCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

var SummaryCardIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${function(props) { return props.bgColor || '#f0f0f0'; }};
    color: ${function(props) { return props.color || '#666'; }};
    font-size: 16px;
`;

var SummaryCardTitle = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 500;
`;

var SummaryCardValue = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    line-height: 1.2;
`;

var SummaryCardCount = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 4px;
`;

var ContasPagarList = function() {
    var dispatch = useDispatch();

    var storeState = useSelector(function(state) {
        return {
            data: state.contasPagar.data,
            summary: state.contasPagar.summary,
            filter: state.contasPagar.filter,
            isLoading: state.contasPagar.isLoading,
        };
    });

    var data = storeState.data;
    var summary = storeState.summary;
    var filter = storeState.filter;
    var isLoading = storeState.isLoading;

    var categories = useSelector(function(state) {
        return state.settings['financeiro.categoria_contas_pagar'] || [];
    });

    var [showDrawer, setShowDrawer] = useState(false);
    var [editingRecord, setEditingRecord] = useState(null);

    // Filtros aplicados: sobrevivem ao refresh por alguns minutos (por escritório/usuário/página).
    var persisted = usePersistedFilters('financeiro.contasPagar', getDefaultContasPagarFilters);
    var applied = persisted.filters;
    var setApplied = persisted.setFilters;
    var defaults = persisted.defaults;
    var activeView = applied.view;
    var search = applied.search || '';
    var selectedMonth = applied.month;
    var customRange = applied.customRange;
    var isPeriodDefault = isContasPagarPeriodDefault(applied, defaults);

    var setActiveView = function(view) {
        setApplied({ view: view });
    };

    var setSearch = function(value) {
        setApplied({ search: value });
    };

    // Período aplicado (mês ou personalizado) — usado na carga e nos recarregamentos.
    var periodRequest = getContasPagarPeriodRequest(applied);

    var reloadCurrentPeriod = function() {
        dispatch(contasPagarActions.loadContasPagar(getContasPagarPeriodRequest(applied)));
    };

    // Carrega o período aplicado: ao montar (restaurado ou padrão) e sempre que ele muda,
    // inclusive ao trocar entre mês e período personalizado.
    useEffect(function() {
        dispatch(contasPagarActions.loadContasPagar(periodRequest));
    }, [periodRequest.from, periodRequest.to]);

    var handleCreate = function() {
        setEditingRecord(null);
        setShowDrawer(true);
    };

    var handleEdit = function(record) {
        setEditingRecord(record);
        setShowDrawer(true);
    };

    var handleDelete = function(record) {
        Modal.confirm({
            title: "Excluir Conta a Pagar",
            content: "Tem certeza que deseja excluir esta conta?",
            okText: "Sim, excluir",
            okType: "danger",
            cancelText: "Cancelar",
            onOk: function() {
                dispatch(contasPagarActions.deleteContaPagar(record.id))
                    .then(function() {
                        message.success("Conta excluída com sucesso!");
                        reloadCurrentPeriod();
                    })
                    .catch(function(err) {
                        var msg = err && err.response && err.response.data && err.response.data.message;
                        message.error(msg || "Erro ao excluir conta.");
                    });
            },
        });
    };

    var handleMarkAsPaid = function(record) {
        Modal.confirm({
            title: "Marcar como Pago",
            content: "Confirma o pagamento desta conta?",
            okText: "Sim, confirmar",
            cancelText: "Cancelar",
            onOk: function() {
                dispatch(contasPagarActions.markAsPaid(record.id, moment().format("YYYY-MM-DD")))
                    .then(function() {
                        message.success("Conta marcada como paga!");
                        reloadCurrentPeriod();
                    })
                    .catch(function() {
                        message.error("Erro ao marcar conta como paga.");
                    });
            },
        });
    };

    var handleSaveSuccess = function() {
        setShowDrawer(false);
        setEditingRecord(null);
        reloadCurrentPeriod();
    };

    var handleSearchChange = function(e) {
        setSearch(e.target.value);
    };

    var handleMonthChange = function(value) {
        setApplied({ month: value, customRange: null });
    };

    var handleCustomRange = function(from, to) {
        setApplied({ customRange: { from: from, to: to } });
    };

    var handleResetPeriod = function() {
        setApplied({ month: defaults.month, customRange: null });
    };

    var filteredData = function() {
        var result = data;

        // Filter by status view
        if (activeView === 'pending') {
            result = result.filter(function(item) {
                return item.status === 'pending';
            });
        } else if (activeView === 'paid') {
            result = result.filter(function(item) {
                return item.status === 'paid';
            });
        } else if (activeView === 'overdue') {
            result = result.filter(function(item) {
                return item.status === 'pending' && moment(item.due_at).isBefore(moment(), 'day');
            });
        }

        // Filter by search term
        if (search) {
            var searchLower = search.toLowerCase();
            result = result.filter(function(item) {
                var description = (item.description || '').toLowerCase();
                var notes = (item.notes || '').toLowerCase();
                return description.indexOf(searchLower) > -1 || notes.indexOf(searchLower) > -1;
            });
        }

        return result;
    };

    var VIEW_LABELS = { all: 'Todas', pending: 'Pendentes', paid: 'Pagos', overdue: 'Vencidas' };
    var VIEW_COLORS = { all: '#1890ff', pending: '#faad14', paid: '#52c41a', overdue: '#f5222d' };
    var periodLabel = customRange
        ? moment(customRange.from).format('DD/MM/YY') + ' - ' + moment(customRange.to).format('DD/MM/YY')
        : 'Mês: ' + moment(selectedMonth, 'YYYY-MM').format('MMM/YYYY');

    var pills = [
        {
            key: 'month', label: periodLabel, color: customRange ? '#722ed1' : '#1890ff', icon: 'calendar',
            onRemove: isPeriodDefault ? undefined : handleResetPeriod,
        },
    ];

    if (activeView !== 'all') {
        pills.push({
            key: 'view', label: VIEW_LABELS[activeView] || activeView, color: VIEW_COLORS[activeView] || '#595959',
            onRemove: function() { setActiveView('all'); },
        });
    }

    if (search) {
        pills.push({
            key: 'search', label: search, color: '#722ed1', icon: 'search',
            onRemove: function() { setSearch(''); },
        });
    }

    var restoredSummary = pills
        .filter(function(pill) { return pill.key !== 'month' || !isPeriodDefault; })
        .map(function(pill) { return pill.label; })
        .join(' · ');

    return (
        <div>
            <ModuleHeaderWrapper>
                <ModuleHeader
                    breadcrumbs={["Financeiro", "Contas a Pagar"]}
                    title="Contas a Pagar"
                    search={
                        <Search
                            value={search}
                            placeholder="Pesquisar"
                            onChange={handleSearchChange}
                        />
                    }
                    center={
                        <>
                            <Popover
                                trigger="click"
                                placement="bottomRight"
                                content={
                                    <div style={{ width: 280 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                            <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                                            <MonthPicker
                                                value={selectedMonth}
                                                onChange={handleMonthChange}
                                                onCustomRange={handleCustomRange}
                                                customRangeLabel={customRange ? moment(customRange.from).format('DD/MM/YY') + ' - ' + moment(customRange.to).format('DD/MM/YY') : null}
                                                showTodayButton={false}
                                            />
                                        </div>

                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {[
                                                    { value: 'all', label: 'Todas', count: summary.count_total || 0, color: '#1890ff' },
                                                    { value: 'pending', label: 'Pendentes', count: summary.count_pending || 0, color: '#faad14' },
                                                    { value: 'paid', label: 'Pagos', count: summary.count_paid || 0, color: '#52c41a' },
                                                    { value: 'overdue', label: 'Vencidas', count: summary.count_overdue || 0, color: '#f5222d' },
                                                ].map(function(opt) {
                                                    var isActive = activeView === opt.value;
                                                    return (
                                                        <div
                                                            key={opt.value}
                                                            onClick={function() { setActiveView(opt.value); }}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                                                background: isActive ? opt.color + '0a' : '#fafafa',
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: isActive ? '#262626' : '#8c8c8c' }}>
                                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                                                {opt.label}
                                                            </span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                                                {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 14 }} />}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                }
                            >
                                <Badge
                                    count={countActiveContasPagarFilters(applied, defaults)}
                                    offset={[-4, 0]}
                                    style={{ backgroundColor: '#722ed1' }}
                                >
                                    <Button
                                        icon="bars"
                                        style={{ borderRadius: 8 }}
                                    >
                                        Filtros
                                    </Button>
                                </Badge>
                            </Popover>
                            <Dropdown
                                trigger={['click']}
                                overlay={
                                    <Menu>
                                        <Menu.Item key="new" onClick={handleCreate}>
                                            <Icon type="plus" /> Nova Conta
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button icon="plus" shape="circle" />
                            </Dropdown>
                        </>
                    }
                    advanced={
                        <FilterPillsBar
                            pills={pills}
                            onClearAll={persisted.isDefault ? undefined : persisted.resetFilters}
                        />
                    }
                />
            </ModuleHeaderWrapper>

            <RestoredFiltersNotice
                visible={persisted.wasRestored}
                summary={restoredSummary}
                onReset={persisted.resetFilters}
                onDismiss={persisted.dismissRestored}
            />

            <Row gutter={16} style={{ paddingTop: 10 }}>
                <Col span={6}>
                    <SummaryCard
                        onClick={function() { setActiveView('all'); }}
                        style={{ cursor: 'pointer', borderColor: activeView === 'all' ? '#1890ff' : '#e8e8e8' }}
                    >
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                        ) : (
                            <>
                                <SummaryCardHeader>
                                    <SummaryCardIcon bgColor="#e6f7ff" color="#1890ff">
                                        <Icon type="wallet" />
                                    </SummaryCardIcon>
                                    <SummaryCardTitle>Total</SummaryCardTitle>
                                </SummaryCardHeader>
                                <SummaryCardValue color="#1890ff">
                                    {toBRL(summary.total || 0)}
                                    <SummaryCardCount>({summary.count_total || 0})</SummaryCardCount>
                                </SummaryCardValue>
                            </>
                        )}
                    </SummaryCard>
                </Col>
                <Col span={6}>
                    <SummaryCard
                        onClick={function() { setActiveView('pending'); }}
                        style={{ cursor: 'pointer', borderColor: activeView === 'pending' ? '#faad14' : '#e8e8e8' }}
                    >
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                        ) : (
                            <>
                                <SummaryCardHeader>
                                    <SummaryCardIcon bgColor="#fffbe6" color="#faad14">
                                        <Icon type="clock-circle" />
                                    </SummaryCardIcon>
                                    <SummaryCardTitle>Pendentes</SummaryCardTitle>
                                </SummaryCardHeader>
                                <SummaryCardValue color="#faad14">
                                    {toBRL(summary.pending || 0)}
                                    <SummaryCardCount>({summary.count_pending || 0})</SummaryCardCount>
                                </SummaryCardValue>
                            </>
                        )}
                    </SummaryCard>
                </Col>
                <Col span={6}>
                    <SummaryCard
                        onClick={function() { setActiveView('paid'); }}
                        style={{ cursor: 'pointer', borderColor: activeView === 'paid' ? '#52c41a' : '#e8e8e8' }}
                    >
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                        ) : (
                            <>
                                <SummaryCardHeader>
                                    <SummaryCardIcon bgColor="#f6ffed" color="#52c41a">
                                        <Icon type="check-circle" />
                                    </SummaryCardIcon>
                                    <SummaryCardTitle>Pagos</SummaryCardTitle>
                                </SummaryCardHeader>
                                <SummaryCardValue color="#52c41a">
                                    {toBRL(summary.paid || 0)}
                                    <SummaryCardCount>({summary.count_paid || 0})</SummaryCardCount>
                                </SummaryCardValue>
                            </>
                        )}
                    </SummaryCard>
                </Col>
                <Col span={6}>
                    <SummaryCard
                        onClick={function() { setActiveView('overdue'); }}
                        style={{ cursor: 'pointer', borderColor: activeView === 'overdue' ? '#f5222d' : '#e8e8e8' }}
                    >
                        {isLoading ? (
                            <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                        ) : (
                            <>
                                <SummaryCardHeader>
                                    <SummaryCardIcon bgColor="#fff1f0" color="#f5222d">
                                        <Icon type="warning" />
                                    </SummaryCardIcon>
                                    <SummaryCardTitle>Vencidas</SummaryCardTitle>
                                </SummaryCardHeader>
                                <SummaryCardValue color="#f5222d">
                                    {toBRL(summary.overdue || 0)}
                                    <SummaryCardCount>({summary.count_overdue || 0})</SummaryCardCount>
                                </SummaryCardValue>
                            </>
                        )}
                    </SummaryCard>
                </Col>
            </Row>

            <TableList
                data={filteredData()}
                isLoading={isLoading}
                categories={categories}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onMarkAsPaid={handleMarkAsPaid}
            />

            <DrawerCreate
                visible={showDrawer}
                onClose={function() { setShowDrawer(false); setEditingRecord(null); }}
                onSuccess={handleSaveSuccess}
                record={editingRecord}
                categories={categories}
            />
        </div>
    );
};

export default ContasPagarList;
